import { Icon, type IconType } from '@assembly-js/design-system';
import { Avatar, SvgIconProps } from '@mui/material';

import {
  AccountIcon,
  AppsIcon,
  BillingIcon,
  BookOpenIcon,
  ClientsIcon,
  ClipboardIcon,
  CompassIcon,
  ContractIcon,
  CustomizationIcon,
  ExternalLinkIcon,
  FilesIcon,
  FormsIcon,
  GiftIcon,
  HardwareChipIcon,
  LinkIcon,
  MessagesIcon,
  MoreActionsIcon,
  NotificationIcon,
  PaymentsIcon,
  PortalModulesIcon,
  PuzzleIcon,
  QuestionIcon,
  TeamIcon,
} from 'src/components/icons';
import { AutomationsIcon } from 'src/components/icons/module-icons/AutomationsIcon';
import { WarningIcon } from 'src/components/icons/module-icons/WarningIcon';
import { PlanIcon } from 'src/components/icons/Plan';
import { StatusIcon } from 'src/components/icons/StatusIcon';
import { StoreIcon } from 'src/components/icons/StoreIcon';
import { AUTH_STATES } from 'src/constants/authConsts';
import {
  REFERRAL_PAID_CREDITS,
  REFERRAL_SIGNUP_CREDITS,
} from 'src/constants/referralConsts';
import {
  CUSTOMIZATION_SETTINGS_PAGE_DESCRIPTION,
  MESSAGING_SETTINGS_PAGE_DESCRIPTION,
  MODULE_MANAGEMENT_DESCRIPTION,
  PAYMENT_LINKS_CREATE_PAGE_DESCRIPTION,
  PAYMENT_LINKS_EDIT_PAGE_DESCRIPTION,
  PAYMENT_LINKS_PAGE_DESCRIPTION,
  PAYMENTS_SETTINGS_PAGE_DESCRIPTION,
  STORE_PAGE_DESCRIPTION,
} from 'src/constants/stringConsts';

const Authentication = (props: SvgIconProps) => (
  <Icon icon="Authentication" {...props} />
);

const AssemblyAvatarIcon = (_: SvgIconProps) => (
  <Avatar
    alt="Assembly"
    src="/images/branding/assembly-logo-icon-black-on-trans.svg"
    className="w-[12px] h-[12px]"
    variant="square"
  />
);

const Web = (props: SvgIconProps) => <Icon icon="Web" {...props} />;

export const COMPANIES_PAGE = {
  path: '/companies',
  label: 'CRM',
  icon: ClientsIcon,
};

export const USERS_PAGE = {
  path: '/clients/users',
  label: 'Clients',
  icon: ClientsIcon,
};

export const INBOX_PAGE = {
  path: '/notifications',
  label: 'Notifications',
  icon: NotificationIcon,
};

export const CRM_CLIENTS_PAGE = {
  path: '/clients/users',
  label: 'CRM',
  icon: ClientsIcon,
};

export const CLIENTS_IMPORT_PAGE = {
  path: '/import',
};

// Both client and company imports share the same route; distinguished via navigation state.
export const COMPANIES_IMPORT_PAGE = CLIENTS_IMPORT_PAGE;

export const HOME_PAGE = {
  path: '/',
  label: 'Dashboard',
  icon: CompassIcon,
};

export const WIDGETS_PAGE = {
  path: '/widgets',
  label: 'Widgets',
};

export const CLIENT_APPS_PAGE = {
  path: '/apps',
  label: '',
};

export const CLIENT_DETAILS_PAGE = {
  path: '/clients/users/details',
  label: 'Client Users',
  icon: ClientsIcon,
};

export const EXPERIENCE_SETUP_PAGE = '/create-ad';

export const EXPERIENCES_PAGE = '/feed';

export const PACKAGES_PAGE = '/packages';

export const ADD_ACCOUNT_PAGE = '/add-account';
export const WORKSPACES_PAGE = '/workspaces';
export const SIGNUP_PAGE = '/signup';
export const ONBOARDING_PAGE = '/onboarding';
export const ADMIN_PAGE = '/admin';
export { LOGIN_PAGE_BASE_PATH } from './loginPath';
export const MCP_LOGIN_PAGE = '/mcp/login';
export const QA_HARNESS_PAGE = '/app-setup/qa-harness';
// Query param name a dispatched QA run's InternalStartURL carries its
// run-scoped bearer under. qa/agent's hasEmbedToken() keys off a query param
// literally named `token`, so this name is load-bearing for that integration.
export const QA_HARNESS_RUN_TOKEN_PARAM = 'token';
export const LOGIN_PAGE = `/login?step=${AUTH_STATES.SIGN_IN}`;
export const AUTH_ACCESS_LINK_PAGE = `/login/access-link`;

export const REGISTER_PAGE = `/login?step=${AUTH_STATES.SIGN_UP}`;

export const REQUIRE_NEW_PASSWORD = `/login?step=${AUTH_STATES.REQUIRE_NEW_PASSWORD}`;

export const CONFIRM_REGISTER_PAGE = `/login?step=${AUTH_STATES.CONFIRM_SIGN_UP}`;

export const FORGOT_PASSWORD_PAGE = `/login?step=${AUTH_STATES.FORGOT_PASSWORD}`;

export const SETTINGS_PAGE = '/settings';

export const ARTICLE_PAGE = '/article';

export const INVOICE_PATH = '/inv';

export const ARTICLE_PATH = '/art';

export const SHORT_LINK_PATH = '/sl';

export const HELPDESK_PAGE = {
  path: '/helpdesk',
  label: 'Helpdesk',
  icon: BookOpenIcon,
};

export const FILES_PAGE = {
  path: '/files',
  label: 'Files',
  icon: FilesIcon,
};
export const ARTICLES_PAGE = {
  path: '/articles',
  label: 'Knowledge base',
  icon: BookOpenIcon,
};
export const PRODUCTS_PAGE = {
  path: '/products',
  label: 'Products',
  icon: PaymentsIcon,
  description: PAYMENTS_SETTINGS_PAGE_DESCRIPTION,
};
export const PAYMENT_PAGE = {
  path: '/payments',
  label: 'Payments',
  icon: PaymentsIcon,
  description: PAYMENTS_SETTINGS_PAGE_DESCRIPTION,
};

export const PAYMENTS_OVERVIEW_PAGE = {
  path: '/payments',
  label: 'Overview',
  icon: PaymentsIcon,
};
export const PAYMENTS_INVOICES_PAGE = {
  path: '/payments/invoices',
  label: 'Invoices',
  icon: PaymentsIcon,
};
export const PAYMENTS_INVOICE_DETAILS_PAGE = {
  path: '/payments/invoices/details',
  label: 'Invoice Details',
  icon: PaymentsIcon,
};
export const PAYMENTS_SUBSCRIPTIONS_PAGE = {
  path: '/payments/subscriptions',
  label: 'Subscriptions',
  icon: PaymentsIcon,
};
export const PAYMENTS_STORE_PAGE = {
  path: '/payments/store',
  label: 'Store',
  icon: StoreIcon,
};
export const PAYMENTS_SERVICES_PAGE = {
  path: '/payments/services',
  label: 'Services',
  icon: PaymentsIcon,
};
export const PAYMENTS_SERVICE_DETAILS_PAGE = {
  path: '/payments/services/details',
  label: 'Service',
};
export const PAYMENTS_SERVICE_EDIT_PAGE = {
  path: '/payments/services/edit',
  label: 'Create Service',
};
export const PAYMENTS_PAYMENT_LINKS_PAGE = {
  path: '/payments/payment-links',
  label: 'Payment Links',
  icon: LinkIcon,
};

export const PAYMENTS_PAYMENT_LINKS_EDIT_PAGE = {
  path: `${PAYMENTS_PAYMENT_LINKS_PAGE.path}/edit`,
  label: 'Edit Payment Link',
  icon: LinkIcon,
};

export const PRODUCT_DETAILS_PAGE = {
  path: '/products/details',
  label: 'Product',
};

export const PRODUCT_EDIT_PAGE = {
  path: '/products/edit',
  label: 'Create Product',
};

export const INVOICES_PAGE = {
  path: '/invoices',
  label: 'Invoices',
  icon: PaymentsIcon,
  description: PAYMENTS_SETTINGS_PAGE_DESCRIPTION,
};

export const CLIENT_BILLING_PAGE = {
  path: '/billing',
  label: 'Billing',
  icon: BillingIcon,
  description: PAYMENTS_SETTINGS_PAGE_DESCRIPTION,
};

export const CLIENT_INVOICE_DETAILS_PAGE = {
  path: '/billing/details',
  label: 'Invoice Details',
  icon: BillingIcon,
};

export const SUBSCRIPTION_PAGE = {
  path: '/subscriptions',
  label: 'Subscriptions',
  icon: PaymentsIcon,
  description: PAYMENTS_SETTINGS_PAGE_DESCRIPTION,
};
export const MESSAGES_PAGE = {
  path: '/messaging',
  label: 'Messages',
  icon: MessagesIcon,
  description: MESSAGING_SETTINGS_PAGE_DESCRIPTION,
};

export const HELP_CENTER_PAGE = {
  path: '/help-center',
  label: 'Help Center',
  icon: QuestionIcon,
};

export const SETTINGS_PAGE_ROOT_PATH = '/settings';

export const CUSTOMIZATION_PAGE = {
  path: '/customization',
  label: 'Customization',
  description: CUSTOMIZATION_SETTINGS_PAGE_DESCRIPTION,
  icon: ExternalLinkIcon,
};

// app setup page is improved version of module management page
export const APP_SETUP_PAGE = {
  path: '/app-setup',
  label: 'App Library',
  description: MODULE_MANAGEMENT_DESCRIPTION,
  icon: CustomizationIcon,
};

export const BILLING_PAGE = {
  path: '/settings/billing',
  label: 'Payment methods',
  icon: BillingIcon,
  description: '',
};

export const PAYMENT_LINKS_PAGE = {
  path: '/payment-links',
  label: 'Payment Links',
  icon: LinkIcon,
  description: PAYMENT_LINKS_PAGE_DESCRIPTION,
};

export const PAYMENT_LINKS_CREATE_PAGE = {
  path: `${PAYMENT_LINKS_PAGE.path}/new`,
  label: 'Create payment link',
  icon: LinkIcon,
  description: PAYMENT_LINKS_CREATE_PAGE_DESCRIPTION,
};

export const PAYMENT_LINKS_EDIT_PAGE = {
  path: `${PAYMENT_LINKS_PAGE.path}/edit`,
  label: 'Edit payment link',
  icon: LinkIcon,
  description: PAYMENT_LINKS_EDIT_PAGE_DESCRIPTION,
};

export const STORE_PAGE = {
  path: '/store',
  label: 'Store',
  icon: StoreIcon,
  description: STORE_PAGE_DESCRIPTION,
};

export const SETUP_APP_PAGE_BASE_PATH = `${APP_SETUP_PAGE.path}/setup` as const;

export const FILES_SETTINGS_PAGE = {
  path: '/settings/files',
  label: 'Files',
  icon: FilesIcon,
  description: '',
};

export const NOTIFICATIONS_SETTINGS_PAGE = {
  path: '/settings/notifications',
  label: 'Notifications',
  icon: NotificationIcon,
  description: '',
};

export const MESSAGING_PAGE = {
  path: '/settings/messaging',
  label: 'Messaging',
  icon: MessagesIcon,
  description: '',
};

export const PAYMENTS_PAGE = {
  path: '/settings/payout-account',
  label: 'Payout account',
  icon: PaymentsIcon,
  description: '',
};

export const PROFILE_PAGE = {
  path: '/settings/profile',
  label: 'Account',
  icon: AccountIcon,
  description: '',
};

export const SETTINGS_TEAM_PAGE = {
  path: '/settings/team',
  label: 'Team',
  icon: TeamIcon,
  description: '',
};

export const SETTINGS_EVENTS_PAGE = {
  path: '/settings/events',
  label: 'Audit Log',
  icon: ClipboardIcon,
  description: '',
};

export const API_SETTINGS_PAGE = {
  path: '/settings/api',
  label: 'API',
  icon: HardwareChipIcon,
  description: '',
};

export const WEBHOOK_SETUP_PAGE = {
  path: '/settings/api/webhooks',
  label: 'Create webhook',
  description: '',
};

export const WEBHOOK_EDIT_PAGE = {
  path: '/settings/api/webhooks/edit',
  label: 'Edit webhook',
  description: '',
};

export const APPS_PAGE = {
  path: '/settings/apps',
  label: 'Integrations',
  icon: AppsIcon,
  description: '',
};

export const CONNECTIONS_PAGE = {
  path: '/settings/connections',
  label: 'Connectors',
  icon: LinkIcon,
  // The mark every nav draws for Connectors; it wins over `icon` where both
  // are given.
  iconName: 'diagram-project' as IconType,
  description:
    'Set up connectors so app users can link personal accounts to apps in this workspace.',
};

export const GENERAL_SETTINGS_PAGE = {
  path: '/settings/general',
  label: 'General',
  icon: PuzzleIcon,
  description: '',
};

export const DANGER_ZONE_PAGE = {
  path: '/settings/danger-zone',
  label: 'Danger zone',
  icon: WarningIcon,
  description: 'Delete the workspace.',
};

export const AUTH_SETTINGS_PAGE = {
  path: '/settings/client-authentication',
  label: 'Client authentication',
  icon: Authentication,
  description: '',
};

export const ASSISTANT_SETTINGS_PAGE = {
  path: '/settings/assistant-settings',
  label: 'Assistant',
  pageLabel: 'Assembly Assistant',
  icon: AssemblyAvatarIcon,
  description: '',
};

export const CLIENT_NOTIFICATIONS_PAGE = {
  path: '/settings/client-notifications',
  label: 'Client notifications',
  icon: NotificationIcon,
  description: '',
};

export const DOMAIN_PAGE = {
  path: '/settings/domains',
  label: 'Custom domains',
  icon: Web,
  pageIcon: Web,
  description: '',
};

export const SIGNATURE_PAGE = {
  path: '/signature',
  label: 'Signature',
};

export const PLAN_PAGE = {
  path: '/settings/plans',
  label: 'Plans',
  icon: PlanIcon,
  pageIcon: PlanIcon,
  description: '',
};

export const EXTENSION_ITEM = {
  label: 'More',
  icon: MoreActionsIcon,
};

export const CLIENT_EXPERIENCE_ITEM = {
  label: 'Portal',
  icon: StatusIcon,
  pageIcon: StatusIcon,
  iconName: 'Dot',
};

export const PORTAL_ITEM = {
  label: 'Customization',
  icon: PortalModulesIcon,
  pageIcon: PortalModulesIcon,
};

export const PLAN_PAGE_USAGE = {
  path: '/settings/plans/usage',
  label: 'Build credit usage',
  description: '',
};

export const PLAN_PAGE_CANCEL_START = {
  path: '/settings/plans/cancel',
  label: 'Are you sure you want to cancel your plan?',
  description: '',
};

export const PLAN_PAGE_CANCEL_CONFIRM = {
  path: '/settings/plans/cancel/confirm',
  label: 'Would you like to pause your subscription instead?',
  description: '',
};

export const PLAN_PAGE_CANCEL_CONFIRM_CALLOUT =
  'You can pause your account for up to 3 months. During this period, you will not be charged, and you and your clients still have full access to your portal. Note that you can only pause your subscription once every 2 years.';

export const REFERRAL_PAGE_V2 = {
  path: '/referrals',
  label: 'Referrals',
  icon: GiftIcon,
  pageIcon: GiftIcon,
  description: 'Refer a friend or colleague and receive Amazon gift cards',
};

// Settings home of the referral program under growth-loops; REFERRAL_PAGE_V2
// is the pre-flag page it replaces.
export const SETTINGS_REFERRALS_PAGE = {
  path: '/settings/referrals',
  label: 'Referrals',
  icon: GiftIcon,
  description: `Refer a firm to get ${REFERRAL_SIGNUP_CREDITS} credits each, and earn ${REFERRAL_PAID_CREDITS} more when they upgrade.`,
};

export const ADD_APP_PAGE = {
  path: '/app-setup/new',
  label: 'Add an App',
};

export const APP_BUILDER_BUILD_TAB_PATH = '/app-setup/build?tab=build';
export const APP_BUILDER_BROWSE_TAB_PATH = '/app-setup/build?tab=browse';
export const APP_BUILDER_SESSION_PATH_PREFIX = '/app-setup/build/session/';

export const ONLY_DESKTOP_SUPPORT_PAGES: Set<string> = new Set<string>();

export const LANDING_HOME_PAGE_TITLE = 'Home';

export const FORMS_PAGE = {
  path: '/forms',
  label: 'Forms',
  icon: FormsIcon,
};

export const FORMS_DETAIL_PAGE_V2 = {
  path: '/forms/details',
};

export const FORMS_RESPONSE_PAGE = {
  path: '/forms/response',
};

export const DEFAULT_SETTINGS_PAGE = 'me';

// these are all the page paths that can lead to different pages based on their
// query param. E.g. the extension path can have multiple items in the sidebar
// that are distinguished by their query param.
export const PagePathsRoutedWithQuery = [CLIENT_APPS_PAGE.path];

// Starting route of path that an IU sees when they want to
// go to a specific portal. E.g. `/portal/portalId`
export const PORTAL_PATH = '/portal';

export const CONTRACTS_PAGE = {
  path: '/contracts',
  label: 'Contracts',
  icon: ContractIcon,
};

export const AUTOMATIONS_PAGE = {
  path: '/automations',
  label: 'Automations',
  icon: AutomationsIcon,
};

export const CONTRACT_DETAILS_PAGE = {
  path: '/contracts/details',
};

export const CONTRACT_SUBMIT_PATH = '/contracts/submit';
export const INVOICE_DETAILS_PAGE_PATH = '/invoices/details';
